import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import sharedStyles from './AdminShared.module.scss';
import styles from './AdminUserWatchHistory.module.scss';
import {
    AnalyticsUserWatchHistoryItem,
    AnalyticsUserWatchHistoryResponse,
    AnalyticsUserWatchTimelineResponse
} from '../../../types/analytics.types';
import { useAnalyticsService } from '../../../services/analytics.service';
import { Routes } from '../../../types/constants';

const dayOptions = [7, 30, 90, 365] as const;
const pageSize = 25;

const formatDateTime = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleString();
};

const formatSeconds = (value?: number | null) => {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return '-';
    }

    const rounded = Math.max(0, Math.round(value));
    const minutes = Math.floor(rounded / 60);
    const seconds = rounded % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const statusClassName = (status: AnalyticsUserWatchHistoryItem['status']) => {
    if (status === 'Completed') {
        return styles.statusCompleted;
    }

    if (status === 'InProgress') {
        return styles.statusInProgress;
    }

    return styles.statusStarted;
};

const AdminUserWatchHistory = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { fetchUserWatchHistory, fetchUserWatchTimeline } = useAnalyticsService();

    const [history, setHistory] = useState<AnalyticsUserWatchHistoryResponse | null>(null);
    const [timeline, setTimeline] = useState<AnalyticsUserWatchTimelineResponse | null>(null);
    const [timelineVideoId, setTimelineVideoId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isTimelineLoading, setIsTimelineLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [timelineError, setTimelineError] = useState<string | null>(null);
    const [days, setDays] = useState(90);
    const [status, setStatus] = useState<'all' | 'started' | 'inprogress' | 'completed'>('all');
    const [sort, setSort] = useState<'lastWatchedAt_desc' | 'progress_desc' | 'title_asc'>('lastWatchedAt_desc');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const routeUserIdRef = useRef(userId);
    const timelineRequestRef = useRef(0);
    routeUserIdRef.current = userId;

    useEffect(() => {
        let active = true;
        const targetUserId = userId;
        timelineRequestRef.current += 1;
        setHistory(null);
        setTimeline(null);
        setTimelineVideoId(null);
        setTimelineError(null);
        setIsTimelineLoading(false);

        const load = async () => {
            if (!targetUserId) {
                setErrorMessage('User was not provided.');
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setErrorMessage(null);
            try {
                const response = await fetchUserWatchHistory(targetUserId, {
                    days,
                    page,
                    pageSize,
                    status,
                    search,
                    sort
                });
                if (!active || routeUserIdRef.current !== targetUserId) return;
                if (response.userId.toLowerCase() !== targetUserId.toLowerCase()) {
                    setErrorMessage('Unable to load watch history.');
                    return;
                }
                setHistory(response);
            } catch {
                if (active && routeUserIdRef.current === targetUserId) {
                    setErrorMessage('Unable to load watch history.');
                }
            } finally {
                if (active && routeUserIdRef.current === targetUserId) {
                    setIsLoading(false);
                }
            }
        };

        void load();
        return () => {
            active = false;
        };
    }, [days, fetchUserWatchHistory, page, search, sort, status, userId]);

    const currentHistory = history && userId && history.userId.toLowerCase() === userId.toLowerCase()
        ? history
        : null;
    const currentTimeline = timeline && userId && timeline.userId.toLowerCase() === userId.toLowerCase()
        ? timeline
        : null;

    const totalPages = useMemo(() => {
        if (!currentHistory || currentHistory.pageSize <= 0) {
            return 1;
        }

        return Math.max(1, Math.ceil(currentHistory.totalCount / currentHistory.pageSize));
    }, [currentHistory]);

    const handleOpenTimeline = async (item: AnalyticsUserWatchHistoryItem) => {
        if (!userId) {
            return;
        }

        const targetUserId = userId;
        const requestId = timelineRequestRef.current + 1;
        timelineRequestRef.current = requestId;
        setTimelineError(null);
        setTimeline(null);
        setIsTimelineLoading(true);
        setTimelineVideoId(item.videoId);
        try {
            const response = await fetchUserWatchTimeline(targetUserId, item.videoId, days, 200);
            if (timelineRequestRef.current !== requestId || routeUserIdRef.current !== targetUserId) return;
            if (response.userId.toLowerCase() !== targetUserId.toLowerCase()
                || response.videoId.toLowerCase() !== item.videoId.toLowerCase()) {
                setTimelineError('Unable to load watch timeline.');
                return;
            }
            setTimeline(response);
        } catch {
            if (timelineRequestRef.current === requestId && routeUserIdRef.current === targetUserId) {
                setTimeline(null);
                setTimelineError('Unable to load watch timeline.');
            }
        } finally {
            if (timelineRequestRef.current === requestId && routeUserIdRef.current === targetUserId) {
                setIsTimelineLoading(false);
            }
        }
    };

    return (
        <div className={sharedStyles.page}>
            <header className={sharedStyles.header}>
                <div>
                    <h1>User Watch History</h1>
                    <p>Review videos watched, progress, and playback timeline per user.</p>
                </div>
                <div className={sharedStyles.actions}>
                    <button
                        type="button"
                        className={sharedStyles.secondary}
                        onClick={() => navigate(
                            currentHistory?.userId
                                ? `${Routes.ADMIN_USERS}/${encodeURIComponent(currentHistory.userId)}`
                                : Routes.ADMIN_USERS,
                            { state: location.state }
                        )}
                    >
                        Back to User
                    </button>
                </div>
            </header>

            {errorMessage ? <div className={sharedStyles.muted}>{errorMessage}</div> : null}

            <section className={sharedStyles.panel}>
                <div className={styles.filters}>
                    <label className={styles.filterLabel}>
                        Days
                        <select
                            value={days}
                            onChange={(event) => {
                                setDays(Number(event.target.value));
                                setPage(1);
                            }}
                        >
                            {dayOptions.map((value) => (
                                <option key={value} value={value}>{value}</option>
                            ))}
                        </select>
                    </label>
                    <label className={styles.filterLabel}>
                        Status
                        <select
                            value={status}
                            onChange={(event) => {
                                setStatus(event.target.value as 'all' | 'started' | 'inprogress' | 'completed');
                                setPage(1);
                            }}
                        >
                            <option value="all">All</option>
                            <option value="started">Started</option>
                            <option value="inprogress">In Progress</option>
                            <option value="completed">Completed</option>
                        </select>
                    </label>
                    <label className={styles.filterLabel}>
                        Sort
                        <select
                            value={sort}
                            onChange={(event) => setSort(event.target.value as 'lastWatchedAt_desc' | 'progress_desc' | 'title_asc')}
                        >
                            <option value="lastWatchedAt_desc">Last Watched</option>
                            <option value="progress_desc">Highest Progress</option>
                            <option value="title_asc">Title A-Z</option>
                        </select>
                    </label>
                    <label className={styles.searchLabel}>
                        Search
                        <input
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            placeholder="Video title or shortcode"
                            maxLength={200}
                        />
                    </label>
                    <button
                        type="button"
                        className={sharedStyles.secondary}
                        onClick={() => {
                            setSearch(searchInput.trim());
                            setPage(1);
                        }}
                    >
                        Apply
                    </button>
                    <button
                        type="button"
                        className={sharedStyles.secondary}
                        onClick={() => {
                            setSearchInput('');
                            setSearch('');
                            setPage(1);
                        }}
                    >
                        Clear
                    </button>
                </div>
            </section>

            {currentHistory ? (
                <section className={sharedStyles.panel}>
                    <h2>{currentHistory.userName} {currentHistory.email ? `(${currentHistory.email})` : ''}</h2>
                    <div className={styles.statsGrid}>
                        <div className={styles.statCard}>
                            <div className={styles.statLabel}>Videos</div>
                            <div className={styles.statValue}>{currentHistory.totalVideos}</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statLabel}>Completed</div>
                            <div className={styles.statValue}>{currentHistory.completedVideos}</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statLabel}>In Progress</div>
                            <div className={styles.statValue}>{currentHistory.inProgressVideos}</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statLabel}>Started Only</div>
                            <div className={styles.statValue}>{currentHistory.startedOnlyVideos}</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statLabel}>Avg Progress</div>
                            <div className={styles.statValue}>{currentHistory.averageProgressPercent.toFixed(1)}%</div>
                        </div>
                    </div>

                    {isLoading ? <div className={sharedStyles.muted}>Loading watch history...</div> : null}
                    {!isLoading && currentHistory.items.length === 0 ? (
                        <div className={sharedStyles.muted}>No watch history for this user in the selected range.</div>
                    ) : null}
                    {!isLoading && currentHistory.items.length > 0 ? (
                        <>
                            <table className={sharedStyles.table}>
                                <thead>
                                    <tr>
                                        <th>Video</th>
                                        <th>Last Watched</th>
                                        <th>Progress</th>
                                        <th>Position</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentHistory.items.map((item) => (
                                        <tr key={item.videoId}>
                                            <td>
                                                <div className={styles.videoTitle}>{item.videoTitle}</div>
                                                <div className={sharedStyles.muted}>{item.videoShortId ?? '-'}</div>
                                            </td>
                                            <td>{formatDateTime(item.lastWatchedAtUtc)}</td>
                                            <td>
                                                <div className={styles.progressTrack}>
                                                    <div
                                                        className={styles.progressFill}
                                                        style={{ width: `${Math.max(0, Math.min(100, item.progressPercent))}%` }}
                                                    />
                                                </div>
                                                <div className={styles.progressLabel}>{item.progressPercent.toFixed(1)}%</div>
                                            </td>
                                            <td>{formatSeconds(item.maxPositionSeconds)} / {formatSeconds(item.durationSeconds)}</td>
                                            <td>
                                                <span className={`${sharedStyles.status} ${statusClassName(item.status)}`}>{item.status}</span>
                                            </td>
                                            <td>
                                                <div className={sharedStyles.rowActions}>
                                                    <button
                                                        type="button"
                                                        className={sharedStyles.secondary}
                                                        onClick={() => handleOpenTimeline(item)}
                                                    >
                                                        Timeline
                                                    </button>
                                                    {item.videoShortId ? (
                                                        <a
                                                            className={styles.linkButton}
                                                            href={`${Routes.WATCH}?v=${encodeURIComponent(item.videoShortId)}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            Open Video
                                                        </a>
                                                    ) : null}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <div className={sharedStyles.actions}>
                                <button
                                    type="button"
                                    className={sharedStyles.secondary}
                                    disabled={currentHistory.page <= 1}
                                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                                >
                                    Prev
                                </button>
                                <span className={sharedStyles.muted}>Page {currentHistory.page} of {totalPages}</span>
                                <button
                                    type="button"
                                    className={sharedStyles.secondary}
                                    disabled={currentHistory.page >= totalPages}
                                    onClick={() => setPage((current) => current + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </>
                    ) : null}
                </section>
            ) : null}

            <section className={sharedStyles.panel}>
                <h2>Watch Timeline</h2>
                {timelineError ? <div className={sharedStyles.muted}>{timelineError}</div> : null}
                {isTimelineLoading ? <div className={sharedStyles.muted}>Loading timeline...</div> : null}
                {!isTimelineLoading && !currentTimeline && !timelineError ? (
                    <div className={sharedStyles.muted}>Select a video row to view the playback timeline.</div>
                ) : null}
                {currentTimeline ? (
                    <>
                        <div className={styles.timelineHeader}>
                            <div>
                                <div className={styles.videoTitle}>{currentTimeline.videoTitle}</div>
                                <div className={sharedStyles.muted}>
                                    {currentTimeline.videoShortId ?? '-'} · {currentTimeline.events.length} events
                                </div>
                            </div>
                            <button
                                type="button"
                                className={sharedStyles.secondary}
                                onClick={() => {
                                    setTimeline(null);
                                    setTimelineVideoId(null);
                                }}
                            >
                                Close Timeline
                            </button>
                        </div>

                        {currentTimeline.events.length === 0 ? (
                            <div className={sharedStyles.muted}>No watch events captured in selected range.</div>
                        ) : (
                            <table className={sharedStyles.table}>
                                <thead>
                                    <tr>
                                        <th>When</th>
                                        <th>Event</th>
                                        <th>Position</th>
                                        <th>Duration</th>
                                        <th>Path</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentTimeline.events.map((event, index) => (
                                        <tr key={`${event.occurredAtUtc}-${event.eventType}-${index}`}>
                                            <td>{formatDateTime(event.occurredAtUtc)}</td>
                                            <td>{event.eventType}</td>
                                            <td>{formatSeconds(event.positionSeconds)}</td>
                                            <td>{formatSeconds(event.durationSeconds)}</td>
                                            <td>{event.path ?? '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </>
                ) : null}
                {timelineVideoId && !currentTimeline && !isTimelineLoading && !timelineError ? (
                    <div className={sharedStyles.muted}>Timeline unavailable for selected video.</div>
                ) : null}
            </section>
        </div>
    );
};

export default AdminUserWatchHistory;
